import React, { useCallback, useEffect, useMemo } from 'react';
import { DateTime } from 'luxon';
import isEmpty from 'lodash/isEmpty';
import cx from 'classnames';
import { Grid } from '~/components';
import { useForm, useLoadSection } from '~/hooks';
import { stringUtils } from '~/util';
const { hasValue } = stringUtils;
import sectionTypes from '../sectionTypes';
import { ValidationErrorMessage } from '../';

const initialState = {
    sectionId: 0,
    sectionDetails: [],
    selectedDeed: {},
    hasErrors: true
};

function validate(data = {}) {
    if (Array.isArray(data?.sectionDetails) && data.sectionDetails.length > 0) {
        return data.sectionDetails.reduce(
            (result, d) => result || validateDeedOfTrust(d),
            false);
    }

    return false;
}

function validateDeedOfTrust(deed) {
    const { 
        book, 
        page, 
        countyRegistry
    } = deed._data;

    return !hasValue(book)
        || !hasValue(page)
        || !hasValue(countyRegistry);
}

const Review = ({ titleOpinionId, handleValidation }) => {

    const { onLoad: loadSection } = useLoadSection(sectionTypes.DEED, titleOpinionId);

    const onLoad = useCallback(async data => {
        try {

            const sectionData = await loadSection(data);
            
            return {
                ...sectionData,
                hasErrors: validate(sectionData)
            };
        } catch (e) {
            console.error(e);
        }
    }, [loadSection]);

    const { 
        state: { values }, 
        fieldActions 
    } = useForm({ initialState, onLoad });

    const selectRow = (data, index) => {
        if (values.selectedDeed && index == values.selectedDeed.index) {
            fieldActions.onChange({}, 'selectedDeed');
        } else {
            return fieldActions.onChange({ ...data, index }, 'selectedDeed');
        }
    };

    const deeds = useMemo(() => {
        return Array.isArray(values.sectionDetails)
            ? values.sectionDetails.map((d,i) => ({ ...d, index: i }) )
            : [];
    }, [values.sectionDetails]);

    useEffect(async () => {
        await handleValidation(values.hasErrors);
    }, [values]);

    return (
        <div className='grid grid-cols-12 gap-2 text-xs text-neutral-90 py-4'>
            { values.hasErrors && (
                <ValidationErrorMessage className='col-span-12' />
            )}
            <Col span={12}>
                <p className='mb-2'>Select a Deed of Trust below to view detailed information</p>
                <DataGrid data={deeds} onRowClick={selectRow} />
            </Col>
            <Col span={12}>
                {!isEmpty(values.selectedDeed) && <DeedofTrustInfo deed={values.selectedDeed}/>}
            </Col>
        </div>
    );
};

export default Review;

const headerClassName = 'bg-gray-50 py-2 px-4 border-b text-left';
const props = {
    className: 'py-1 px-4 border-t text-xs text-neutral-70'
};

function DataGrid({data, onRowClick, className}) {
    const columns = [
        {
            title: 'Deeds of Trust',
            cell: ({ data }) => {
                const hasErrors = validateDeedOfTrust(data);

                return (
                    <span className={cx({ ['text-error-60']: hasErrors })}>
                        {`Deed of Trust #${data.index + 1}`}
                    </span>
                );
            },
            canSort: false,
            headerClassName,
            props
        }
    ];

    return (
        <Grid 
            columns={columns} 
            data={data} 
            className={className}
            rowSelect
            events={{
                onRowClick
            }}
        />
    );
}

const currencyFormatter = Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function DeedofTrustInfo({ deed }) {
    const { _data, index } = deed;

    let securedAmount = '';
    if (hasValue(_data.securingAmountOf)) {
        if (/[^0-9]/i.test(_data.securingAmountOf)) {
            securedAmount = _data.securingAmountOf;
        } else {
            securedAmount = currencyFormatter.format(_data.securingAmountOf);
        }
    }

    const dated = parseDateString(_data.dated);

    const recordedOn = hasValue(_data.recordedOn)
        ? DateTime.fromISO(_data.recordedOn).toLocal()
        : '';
    const bookAndPage = parseBookAndPage(
        _data.book, _data.page
    );

    const reRecordedOn = parseDateString(_data.reRecordedOn);
    const reBookAndPage = parseBookAndPage(
        _data.reBook, _data.rePage
    );
    const assignedBookAndPage = parseBookAndPage(
        _data.assignedBook, _data.assignedPage
    );

    const lienOrder = isEmpty(_data.lienOrder)
        ? '___'
        : _data.lienOrder.label;

    const docs = Array.isArray(deed.documents) ? deed.documents : [];

    const countyError = !hasValue(_data.countyRegistry);
    const bookPageError = !hasValue(_data.book) || !hasValue(_data.page);

    return (
        <div className='grid grid-cols-12 mt-4 gap-2'>
            <Col span={12} className='px-2 border-b border-gray-290 mb-2'>
                <p className='text-sm font-medium'>Deed of Trust #{index+1}</p>
            </Col>
            <Col span={12} className='flex flex-col mb-2'>
                <p className='font-semibold text-neutral-100'>Mortgator(s)</p>
                <p>{_data.mortgators}</p>
            </Col>
            <Col span={12} className='flex flex-col mb-2'>
                <p className='font-semibold text-neutral-100'>Beneficiary</p>
                <p>{_data.beneficiary}</p>
            </Col>
            <Col span={12} className='flex flex-col mb-2'>
                <p className='font-semibold text-neutral-100'>Trustee</p>
                <p>{_data.trustee}</p>
            </Col>
            <Col span={4} className='col-start-1 flex items-center mb-1'>
                <p className='font-semibold text-neutral-100 mr-6'>Securing Principal Amount Of:</p>
                <p>{securedAmount}</p>
            </Col>
            <Col span={3} className='col-start-1 flex items-center mb-1'>
                <p className='font-semibold text-neutral-100 mr-6'>Dated</p>
                <p>{dated}</p>
            </Col>
            <Col span={3} className='flex items-center mb-1'>
                <p className='font-semibold text-neutral-100 mr-4'>Recorded On</p>
                <p>{recordedOn.toLocaleString()}</p>
                <p className='font-semibold text-neutral-100 mx-4'>At</p>
                <p>{recordedOn.toLocaleString(DateTime.TIME_SIMPLE)}</p>
            </Col>
            <Col span={3} className='flex items-center mb-1'>
                <p className={cx('font-semibold mr-6', bookPageError ? 'text-error-60' : 'text-neutral-100')}>
                    Book &amp; Page
                </p>
                <p>{bookAndPage}</p>
            </Col>
            <Col span={3} className='flex items-center mb-1'>
                <p className={cx('font-semibold mr-6', countyError ? 'text-error-60' : 'text-neutral-100')}>
                    County Registry
                </p>
                <p>{_data.countyRegistry}</p>
            </Col>
            <Col span={3} className='col-start-1 flex items-center mb-1'>
                <p className='font-semibold text-neutral-100 mr-6'>Re-recorded On</p>
                <p>{reRecordedOn}</p>
            </Col>
            <Col span={3} className='flex items-center mb-1'>
                <p className='font-semibold text-neutral-100 mr-6'>Book &amp; Page</p>
                <p>{reBookAndPage}</p>
            </Col>
            <Col span={3} className='flex items-center mb-1'>
                <p className='font-semibold text-neutral-100 mr-6'>County Registry</p>
                <p>{_data.reCountyRegistry}</p>
            </Col>
            <Col span={3} className='col-start-1 flex items-center mb-1'>
                <p className='font-semibold text-neutral-100 mr-6'>Assigned To</p>
                <p>{_data.assignedTo}</p>
            </Col>
            <Col span={3} className='flex items-center mb-1'>
                <p className='font-semibold text-neutral-100 mr-6'>Book &amp; Page</p>
                <p>{assignedBookAndPage}</p>
            </Col>
            <Col span={3} className='flex items-center mb-1'>
                <p className='font-semibold text-neutral-100 mr-6'>County Registry</p>
                <p>{_data.assignedCountyRegistry}</p>
            </Col>
            <Col span={12} className='flex items-center mb-1'>
                <p className='font-semibold text-neutral-100 mr-6'>
                    The Deed of Trust identified above is a valid {lienOrder} lien on the property.
                </p>
            </Col>
            <Col span={12} className='flex items-center mb-1'>
                <p className='font-semibold text-neutral-100 mr-6'>Copy of Deed of Trust</p>
                <p>{docs.map((doc, i) => <span key={`dot-copy-${i}`}>{doc.name}</span>)}</p>
            </Col>
        </div>
    );
}

function parseDateString(value) {
    return hasValue(value)
        ? DateTime.fromISO(value).toLocal().toLocaleString()
        : '';
}

function parseBookAndPage(book, page) {
    if (!hasValue(book) && !hasValue(page))
        return '';
    
    const labeledBook = hasValue(book)
        ? `Book ${book}`
        : '';
    const labeledPage = hasValue(page)
        ? `Page ${page}`
        : '';

    return [labeledBook, labeledPage]
        .filter(v => hasValue(v))
        .join(',');
}

function Col({ children, span = 1, className = '' }) {
    return (
        <div className={`col-span-${span} ${className}`}>
            {children}
        </div>
    );
}